import { useState } from "react";
import { useMirKv, type MirKvGame } from "../../hooks/useMirKv";
import type { DateRange } from "../../components/DateSelector";
import { PlayersBadge } from "../../components/shared";
import css from "./dcSections.module.css";

const MON = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
/* 5.0/✕ — чисто интерфейсные метки операторов для агрегатора (дальше живой человек
   работает сам); ни из чего не выводятся, категорий «нет отчёта/без отметки» НЕТ.
   Отмена самой брони — отдельный бейдж: игры в реальности не было (26.08, владелец). */

/**
 * DC «Мир Кв» (screen-06, переделка 26.08 по владельцу): игры «Мира Квестов»
 * за ВЫБРАННУЮ дату календаря; исход — по МК-отметкам операторов (5.0/✕ из
 * отчётов), в развороте — отметки и комментарии ВСЕХ операторов игры.
 */
export function DcMirKvSection({ range }: { range: DateRange }) {
  const { games, kpi, loading, error, oneDay } = useMirKv(range);

  return (
    <div className={css.main}>
      {error ? (
        <div className={css.err}>Ошибка загрузки: {error}</div>
      ) : loading ? (
        <div className={css.loading}>Загрузка…</div>
      ) : (
        <>
          <div className={css.mkKpi}>
            <div className={css.mkKpiHead}>Мир Квестов</div>
            <div className={css.mkTiles}>
              <Tile n={kpi.games} l="Игр" cls="" />
              <Tile n={kpi.score} l="Метка 5.0" cls="score" />
              <Tile n={kpi.cancel} l="Метка ✕" cls="cancel" />
            </div>
            <div className={css.mkRev}>
              {/* 19.09 (владелец): во вкладке — деньги только за игру; чайная, МК и прочее — по тапу */}
              <span className={css.mkRevL}>{oneDay ? "За игры за день" : "За игры за период"}</span>
              <span className={css.mkRevV}>₽{fmtMoney(kpi.revenue)}</span>
            </div>
            <div className={css.mkRevNote}>Только за игру — без чайной, мастер-классов и других услуг. Тап по сумме игры — весь расчёт.</div>
          </div>

          {games.length === 0 ? (
            <div className={css.empty}>Игр Мир Квестов на эту дату нет</div>
          ) : (
            games.map((g) => <MkCard key={String(g.id)} g={g} />)
          )}
        </>
      )}
    </div>
  );
}

function Tile({ n, l, cls }: { n: number; l: string; cls: string }) {
  return (
    <div className={css.mkTile}>
      <span className={`${css.mkNum} ${cls ? css[`mk_${cls}`] : ""}`}>{n}</span>
      <span className={css.mkLbl}>{l}</span>
    </div>
  );
}

function MkCard({ g }: { g: MirKvGame }) {
  const [open, setOpen] = useState(false);
  const time = (g.custom_time || g.time || "").slice(0, 5);
  const quest = g.short_name || g.quest_name || "—";
  const hide = (v?: string | null) => { const s = (v ?? "").trim(); return s === "[encrypted]" ? "" : s; };
  const client = hide(g.client_name); const phone = hide(g.phone);
  const hasReports = g.reports.length > 0;
  return (
    <div className={`${css.mkCard} ${open ? css.mkOpen : ""}`}>
      <button type="button" className={css.mkHead} onClick={() => hasReports && setOpen((o) => !o)} disabled={!hasReports}>
        <span className={css.mkWhen}>
          <span className={css.mkD}>{fmtDM(g.date)}</span>
          <span className={css.mkT}>{time}</span>
        </span>
        <span className={css.mkQname}>{quest}</span>
        {g.status === "cancelled" ? <span className={css.mkBkCancel} title="Бронь отменена через сетку — игры в реальности не было">игры РЕАЛЬНО не было</span> : null}
        {/* метки — тем же рисунком, что бейджи в отчётах ОО: красный ✕, золотой квадратик 5.0 со звездой */}
        {g.has_cancel ? <span className={`${css.mkMark} ${css.mkMarkX}`} title="Метка операторов: отменить на МК">✕</span> : null}
        {g.has_score ? <span className={`${css.mkMark} ${css.mkMark5}`} title="Метка операторов: оценка 5.0"><svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 2l1.8 3.7L14 6.3l-3 2.9.7 4.1L8 11.4 4.3 13.3 5 9.2 2 6.3l4.2-.6z" /></svg>5.0</span> : null}
        {hasReports ? <span className={css.mkChev} aria-hidden="true">▾</span> : null}
      </button>
      <div className={css.mkRowData}>
        {/* 05.09 (владелец): стандартный бейдж игроков; g.id = bookings.id (/api/bookings/mirkv) — клик открывает карточку клиента */}
        {g.players ? <PlayersBadge count={g.players} actual={g.actual_players} bookingId={Number(g.id)} fallbackName={client} size="sm" /> : null}
        {g.branch_name ? <span className={css.cxBranch}>{g.branch_name}</span> : null}
        {g.created_at ? <span className={css.mkCreated} title="Когда бронь поставили">поставлена {fmtWhen(utc5(g.created_at))}</span> : null}
        {/* 19.09 (владелец): на карточке — только деньги за игру; тап раскрывает полный расчёт */}
        <MoneyCell g={g} />
      </div>
      {client || phone ? (
        <div className={css.mkClientRow}>
          {client ? <span className={css.cxClient}>{client}</span> : null}
          {phone ? <span className={css.cxPhone}>{phone}</span> : null}
        </div>
      ) : null}
      {open && hasReports ? (
        <div className={css.mkReports}>
          {g.reports.map((r, i) => (
            <div key={i} className={css.mkReport}>
              <div className={css.mkRepAuthor}>
                {r.is_primary ? <span className={css.mkStar}>★</span> : null}
                <b>{r.operator_name || "—"}</b>
                {r.mk_status === "score" ? <span className={`${css.mkRepMark} ${css.score}`}>5.0</span>
                  : r.mk_status === "cancel" ? <span className={`${css.mkRepMark} ${css.cancel}`}>✕ отмена</span> : null}
                {r.created_at ? <span className={css.when}> · {fmtWhen(r.created_at)}</span> : null}
              </div>
              {r.comment ? <div className={css.mkRepText}>{r.comment}</div> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function fmtMoney(n: number): string {
  return Math.round(n || 0).toLocaleString("ru-RU");
}
/* Деньги игры (19.09, владелец): в строке — только «за игру», тап открывает весь расчёт:
   сколько за что (игра, ночная надбавка, чайная, МК) и чем оплачено. */
function MoneyCell({ g }: { g: MirKvGame }) {
  const [open, setOpen] = useState(false);
  const m = g.money;
  const game = m ? m.game : Number(g.revenue) || 0;
  const extras = m ? m.tea + m.mc : 0;
  const rows: [string, number][] = m ? [
    ["Игра", m.quest], ["Ночная надбавка", m.night], ["Чайная", m.tea], ["Мастер-класс", m.mc],
  ] : [];
  const pays: [string, number][] = m ? [
    ["Наличные", m.cash], ["Терминал", m.terminal], ["Перевод", m.transfer],
    ["Задаток", m.prepaid], ["Счёт", m.invoice], ["Прочее", m.other], ["Сдача", -m.change],
  ] : [];
  /* Раскрываем строкой на всю ширину карточки (как было у «на месте + задаток»), а не
     всплывашкой: на телефоне абсолютный попап уезжал за край экрана. */
  return (<>
    <button type="button" className={css.mkPrice} onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      title="Только за игру · тап — весь расчёт" aria-expanded={open}>
      ₽{fmtMoney(game)}
      {extras > 0 ? <i className={css.mkExtraDot} aria-hidden="true">+</i> : null}
    </button>
    {open && m ? (
      <div className={css.mkMoneyPop} onClick={(e) => e.stopPropagation()}>
        <div className={css.mkMoneyHead}>{m.hasReport ? "Сколько за что" : "Отчёта ещё нет — ожидание по брони"}</div>
        {rows.filter(([, v]) => v > 0).map(([l, v]) => (
          <div key={l} className={css.mkMoneyRow}><span>{l}</span><b>₽{fmtMoney(v)}</b></div>
        ))}
        <div className={`${css.mkMoneyRow} ${css.mkMoneyTot}`}><span>Итого</span><b>₽{fmtMoney(m.total)}</b></div>
        {m.hasReport ? (<>
          <div className={css.mkMoneyHead}>Чем оплатили</div>
          {pays.filter(([, v]) => v !== 0).map(([l, v]) => (
            <div key={l} className={css.mkMoneyRow}><span>{l}</span><b>₽{fmtMoney(v)}</b></div>
          ))}
        </>) : null}
      </div>
    ) : null}
  </>);
}

function fmtDM(date?: string | null): string {
  if (!date) return "";
  const [, m, d] = date.split("-");
  if (!m || !d) return date;
  return `${Number(d)} ${MON[Number(m) - 1] ?? m}`;
}
function fmtWhen(iso: string): string {
  const d = iso.slice(0, 10);
  const t = iso.slice(11, 16);
  return `${fmtDM(d)}${t ? ` ${t}` : ""}`;
}
/** created_at в БД — UTC («YYYY-MM-DD HH:MM:SS»); показываем по Оренбургу (+5). */
function utc5(iso: string): string {
  const ms = Date.parse(iso.replace(" ", "T") + (iso.includes("Z") || iso.includes("+") ? "" : "Z"));
  return Number.isFinite(ms) ? new Date(ms + 5 * 3600000).toISOString() : iso;
}
